import { useEffect, useMemo, useState } from 'react';
import { useSidebar } from '../contexts/SidebarContext';
import { listRooms, getRoomImages, updateRoom, deleteRoom } from '../api/rooms';

const ITEMS_PER_PAGE = 12;

/**
 * Projects page ( /projects )
 * Similar to RecentProjects, but shows a paginated list of room cards.
 */
const Projects = () => {
  const { setCurrentView, setRoomId, setPhotorealisticImage } = useSidebar();

  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [menuRoomId, setMenuRoomId] = useState(null);
  const [editingRoomId, setEditingRoomId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState(null);
  /** Shown when delete (or other non-modal action) fails */
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    listRooms()
      .then((data) => {
        if (cancelled) return;

        let items = [];
        if (Array.isArray(data)) items = data;
        else if (Array.isArray(data?.results)) items = data.results;
        else if (Array.isArray(data?.rooms)) items = data.rooms;

        const sorted = [...items].sort((a, b) => {
          const aDate = a.updatedAt || a.createdAt || 0;
          const bDate = b.updatedAt || b.createdAt || 0;
          return new Date(bDate).getTime() - new Date(aDate).getTime();
        });

        setRooms(sorted);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Projects: GET /rooms failed:', err);
        setError(err?.message || 'Failed to load projects');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (editingRoomId == null) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setEditingRoomId(null);
        setModalError(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editingRoomId]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(rooms.length / ITEMS_PER_PAGE)), [rooms.length]);

  useEffect(() => {
    // If user is on a higher page after data loads, clamp.
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRooms = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return rooms.slice(start, start + ITEMS_PER_PAGE);
  }, [rooms, page]);

  // Fetch thumbnails for visible page items only.
  useEffect(() => {
    const ids = pageRooms.map((r) => r.id).filter((id) => id != null);
    if (ids.length === 0) return;

    ids.forEach((id) => {
      getRoomImages(id)
        .then((images) => {
          if (!Array.isArray(images) || images.length === 0) return;

          setRooms((prev) => {
            const room = prev.find((r) => r.id === id);
            if (!room) return prev;

            const latestImageId =
              room.latestRoomImageId ?? room.latestImageId ?? room.roomImageId ?? null;

            let chosen = null;
            if (latestImageId != null) {
              chosen =
                images.find(
                  (img) => img.id === latestImageId || img.roomImageId === latestImageId
                ) ?? null;
            }

            if (!chosen) {
              const sortedImgs = [...images].sort((a, b) => {
                const at = a.createdAt ?? '';
                const bt = b.createdAt ?? '';
                return new Date(bt) - new Date(at);
              });
              chosen = sortedImgs[0] ?? null;
            }

            const url = chosen?.s3Key ?? null;
            if (!url) return prev;

            return prev.map((r) =>
              r.id === id ? { ...r, _thumbnailUrl: url } : r
            );
          });
        })
        .catch((err) => {
          console.error(`Projects: GET /rooms/${id}/images failed:`, err);
        });
    });
  }, [page, rooms.length]);

  const formatDate = (raw) => {
    if (!raw) return '';
    const dateVal = typeof raw === 'string' ? new Date(raw) : raw;
    if (!dateVal || Number.isNaN(dateVal.getTime?.())) return '';
    return dateVal.toLocaleDateString();
  };

  return (
    <div className="mb-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Projects</h2>

      {actionError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
          {actionError}
        </div>
      )}

      {loading && (
        <div className="text-sm text-gray-500 py-6">Loading projects...</div>
      )}

      {error && (
        <div className="text-sm text-red-600 py-2">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pageRooms.map((item) => {
              const id = item.id;
              const title = item.name ?? item.title ?? `Room ${id}`;
              const created = formatDate(item.createdAt);
              const thumbnailUrl = item._thumbnailUrl;
              const isMenuOpen = menuRoomId === id;

              return (
                <div
                  key={id}
                  className="bg-gray-100 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                  onClick={() => {
                    setRoomId(id);
                    setPhotorealisticImage(null);
                    setCurrentView('Photorealistic');
                  }}
                >
                  <div className="aspect-video bg-gray-200 flex items-center justify-center relative overflow-hidden">
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt={title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                        draggable={false}
                        crossOrigin="anonymous"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          setRooms((prev) =>
                            prev.map((r) =>
                              r.id === id ? { ...r, _thumbnailUrl: null } : r
                            )
                          );
                        }}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <svg
                          className="w-24 h-24 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-800 mb-1 group-hover:text-primary-200 transition-colors truncate">
                          {title}
                        </h3>
                        {created && (
                          <p className="text-xs text-gray-500">
                            Created: {created}
                          </p>
                        )}
                      </div>
                      <div className="relative shrink-0 z-10">
                        <button
                          type="button"
                          className="p-1 rounded hover:bg-gray-200 transition-colors"
                          aria-expanded={isMenuOpen}
                          aria-haspopup="true"
                          aria-label="Project actions"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuRoomId(isMenuOpen ? null : id);
                          }}
                        >
                          <svg
                            className="w-4 h-4 text-gray-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                            />
                          </svg>
                        </button>
                        {isMenuOpen && (
                          <div className="absolute bottom-full right-0 mb-1 w-40 bg-white border border-gray-200 rounded shadow-lg z-20">
                            <button
                              type="button"
                              className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuRoomId(null);
                                setEditingRoomId(id);
                                setEditName(item.name ?? title);
                                setEditDescription(item.description ?? '');
                                setModalError(null);
                              }}
                            >
                              Update Details
                            </button>
                            <button
                              type="button"
                              className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50"
                              onClick={async (e) => {
                                e.stopPropagation();
                                setMenuRoomId(null);
                                if (!window.confirm('Are you sure you want to delete this room?')) return;
                                setActionError(null);
                                try {
                                  await deleteRoom(id);
                                  setRooms((prev) => prev.filter((r) => r.id !== id));
                                } catch (err) {
                                  console.error('Projects: delete room failed:', err);
                                  setActionError(err?.message ?? 'Failed to delete room');
                                }
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex items-center justify-between gap-4">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-4 py-2 rounded-lg bg-gray-800 text-white text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            <div className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </div>

            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-4 py-2 rounded-lg bg-gray-800 text-white text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </>
      )}

      {editingRoomId != null && (() => {
        const room = rooms.find((r) => r.id === editingRoomId);
        const roomTitle = room?.name ?? room?.title ?? `Room ${editingRoomId}`;
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => {
              setEditingRoomId(null);
              setModalError(null);
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="projects-update-details-title"
          >
            <div
              className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2
                id="projects-update-details-title"
                className="text-lg font-semibold text-gray-800 mb-4"
              >
                Update room details
              </h2>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="projects-edit-room-name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Name
                  </label>
                  <input
                    id="projects-edit-room-name"
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                    placeholder="e.g. Living Room"
                    autoFocus
                  />
                </div>
                <div>
                  <label
                    htmlFor="projects-edit-room-desc"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Description
                  </label>
                  <textarea
                    id="projects-edit-room-desc"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent resize-y min-h-[80px]"
                    placeholder="Optional description"
                    rows={3}
                  />
                </div>
                {modalError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {modalError}
                  </p>
                )}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    disabled={saving}
                    className="flex-1 py-2.5 px-4 rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    onClick={async () => {
                      setModalError(null);
                      setSaving(true);
                      try {
                        await updateRoom(editingRoomId, {
                          name: editName.trim() || roomTitle,
                          description: editDescription.trim(),
                          latestImageId: room?.latestImageId ?? room?.roomImageId ?? null,
                        });
                        setRooms((prev) =>
                          prev.map((r) =>
                            r.id === editingRoomId
                              ? {
                                  ...r,
                                  name: editName.trim() || roomTitle,
                                  description: editDescription.trim(),
                                }
                              : r
                          )
                        );
                        setEditingRoomId(null);
                      } catch (err) {
                        console.error('Projects: update room failed:', err);
                        setModalError(err?.message ?? 'Failed to update room');
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    className="px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 disabled:opacity-60 transition-colors"
                    onClick={() => {
                      setEditingRoomId(null);
                      setModalError(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Projects;

